import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

export const usePlacesService = ({ enable, debounceMs = 400 }: { enable: boolean; debounceMs?: number }) => {
  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null);
  const [placePredictions, setPlacePredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [placeInputValue, setPlaceInputValue] = useState<string>('');
  const [isPlacePredictionsLoading, setIsPlacePredictionsLoading] = useState(false);

  const getPlacePredictions = useMemo(
    () =>
      debounce((request: google.maps.places.AutocompletionRequest) => {
        autocompleteService.current?.getPlacePredictions(request, (result) => {
          if (result) {
            setPlacePredictions(result);
          }
          setIsPlacePredictionsLoading(false);
        });
      }, debounceMs),
    [],
  );

  const reset = () => {
    setPlacePredictions([]);
  };

  useEffect(() => {
    if (enable) {
      if (!autocompleteService.current && (window as any).google) {
        autocompleteService.current = new google.maps.places.AutocompleteService();
      }
      if (!placesService.current && (window as any).google) {
        placesService.current = new google.maps.places.PlacesService(document.createElement('div'));
      }
    }
  }, [enable]);

  return {
    autocompleteService: autocompleteService.current,
    placesService: placesService.current,
    placePredictions: placeInputValue ? placePredictions : [],
    getPlacePredictions: (opt: google.maps.places.AutocompletionRequest) => {
      if (opt.input) {
        setPlaceInputValue(opt.input);
        setIsPlacePredictionsLoading(true);
        getPlacePredictions(opt);
        return;
      }
      setPlacePredictions([]);
      setPlaceInputValue('');
      getPlacePredictions(opt);
      setIsPlacePredictionsLoading(false);
    },
    resetPlacePredictions: reset,
    isPlacePredictionsLoading,
  };
};
